import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import strings from "../../../../utils/strings";
import Widgets from "../../../../widgets";
import "./ManageRequestServiceDetail.css";
import Icons from "../../../../Assets/Icons";
import { uploadMedia } from "../../../../modules/patientProfile/patientProfileThunk";
import {
  fetchServices,
  fetchSubServices,
  getServiceItemById,
  updateServiceItem,
  deleteServiceItem,
  updateService,
  deleteService,
} from "../../../../modules/NursignCare/ManageServices/ManageServiceThunk";
import {
  resetGetServiceItemById,
  resetUpdateServiceItem,
  clearSubServices,
} from "../../../../modules/NursignCare/ManageServices/ManageServiceSlice";
import {
  getLocalizedTitle,
  translateManageServiceToast,
  uniqueByLocalizedTitle,
} from "../../../../utils/manageServiceValidation";

const ManageRequestServiceDetails = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Get ID from navigation state
  const item = location.state;
  const id = item?._id;

  // Language state - sync with global language
  const [activeLang, setActiveLang] = useState(
    i18n.language === "vietnam" ? "vi" : "en"
  );

  // Sync with global language
  useEffect(() => {
    const lang = i18n.language === "vietnam" ? "vi" : "en";
    setActiveLang(lang);
  }, [i18n.language]);

  // Form state
  const [formData, setFormData] = useState({
    title: {
      en: "",
      vi: "",
    },
    service: "",
    subService: "",
    code: "",
    price: {
      type: "PER_TIME",
      wh: {
        opdInternationalPrice: "",
        opdLocalPrice: "",
        opdSubsidized: "",
      },
      nwh: {
        opdInternationalPrice: "",
        opdLocalPrice: "",
        opdSubsidized: "",
      },
    },
    checklist: "",
  });

  const [pdfUrl, setPdfUrl] = useState("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [isPriceDirty, setIsPriceDirty] = useState(false);

  // Redux state
  const {
    services,
    subServices,
    serviceItemDetail,
    getServiceItemByIdSuccess,
    getServiceItemByIdErr,
    updateServiceItemSuccess,
    updateServiceItemErr,
  } = useSelector((state) => state.manageService);

  // Language dropdown options
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "vi", label: "Vietnamese" },
  ];

  // Service Type options
  const serviceTypeOptions = [
    { value: "PER_TIME", label: t("perTime") },
    { value: "PER_HOUR", label: t("perHour") },
  ];

  // Fetch services on mount
  useEffect(() => {
    dispatch(fetchServices({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Fetch service item by ID
  useEffect(() => {
    if (item?._id) {
      dispatch(getServiceItemById({
        _id: item._id,
        service: item.service?._id || item.service,
        subService: item.subService?._id || item.subService,
      }));
    }
  }, [item, dispatch]);

  // Prefill form when data is loaded
  useEffect(() => {
    if (getServiceItemByIdSuccess && getServiceItemByIdSuccess.status === "success") {
      const data =
        getServiceItemByIdSuccess?.data?.serviceItems?.[0] ||
        getServiceItemByIdSuccess?.data?.serviceItem;
      if (data) {
        setExistingData(data);
        setPdfUrl(data.checklist || "");

        const normalize = (v) => (v !== null && v !== undefined ? String(v) : "");
        const initialPrice = {
          type: data.price?.type || "PER_TIME",
          wh: {
            opdInternationalPrice: normalize(data.price?.wh?.opdInternationalPrice),
            opdLocalPrice: normalize(data.price?.wh?.opdLocalPrice),
            opdSubsidized: normalize(data.price?.wh?.opdSubsidized),
          },
          nwh: {
            opdInternationalPrice: normalize(data.price?.nwh?.opdInternationalPrice),
            opdLocalPrice: normalize(data.price?.nwh?.opdLocalPrice),
            opdSubsidized: normalize(data.price?.nwh?.opdSubsidized),
          },
        };

        setOriginalPrice(initialPrice);
        setFormData({
          title: {
            en: data.title?.en || "",
            vi: data.title?.vi || "",
          },
          service: (typeof data.service === 'object' ? data.service?._id || data.service?.id : data.service) || "",
          subService: (typeof data.subService === 'object' ? data.subService?._id || data.subService?.id : data.subService) || "",
          code: data.code || "",
          price: initialPrice,
          checklist: data.checklist || "",
        });
      }
      dispatch(resetGetServiceItemById());
    }
  }, [getServiceItemByIdSuccess, dispatch]);

  useEffect(() => {
    if (!originalPrice) return;
    const current = formData.price;
    const isDifferent =
      originalPrice.type !== current.type ||
      originalPrice.wh.opdInternationalPrice !== current.wh.opdInternationalPrice ||
      originalPrice.wh.opdLocalPrice !== current.wh.opdLocalPrice ||
      originalPrice.wh.opdSubsidized !== current.wh.opdSubsidized ||
      originalPrice.nwh.opdInternationalPrice !== current.nwh.opdInternationalPrice ||
      originalPrice.nwh.opdLocalPrice !== current.nwh.opdLocalPrice ||
      originalPrice.nwh.opdSubsidized !== current.nwh.opdSubsidized;
    setIsPriceDirty(isDifferent);
  }, [formData.price, originalPrice]);

  // Handle errors
  useEffect(() => {
    if (getServiceItemByIdErr) {
      dispatch(resetGetServiceItemById());
    }
    if (updateServiceItemErr) {
      Widgets.ToastPopup.error(translateManageServiceToast(updateServiceItemErr, t));
      dispatch(resetUpdateServiceItem());
    }
    if (updateServiceItemSuccess && updateServiceItemSuccess.status === "success") {
      Widgets.ToastPopup.success(t("serviceItemUpdated") || "Service item updated successfully");
      navigate(strings.navigation.nursingCareManageService);
      dispatch(resetUpdateServiceItem());
    }
  }, [getServiceItemByIdErr, updateServiceItemSuccess, updateServiceItemErr, dispatch, t, navigate]);

  // Fetch sub-services when service changes
  useEffect(() => {
    if (formData.service) {
      dispatch(fetchSubServices(formData.service));
    } else {
      dispatch(clearSubServices());
      setFormData((prev) => ({ ...prev, subService: "" }));
    }
  }, [formData.service, dispatch]);

  // Helper function to get locale string
  const getLocale = (title) => {
    if (!title) return "";
    if (typeof title === "string") return title;
    return title[activeLang] || title["en"] || title["vi"] || "";
  };

  // Handle title change
  const handleTitleChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title: {
        ...prev.title,
        [activeLang]: value,
      },
    }));
  };

  // Handle service type change
  const handleServiceTypeChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      price: {
        ...prev.price,
        type: option.value,
      },
    }));
  };

  // Handle service change
  const handleServiceChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      service: option.value,
      subService: "", // Reset subService when service changes
    }));
  };

  // Handle sub-service change
  const handleSubServiceChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      subService: option.value,
    }));
  };

  // Handle code change
  const handleCodeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      code: e.target.value,
    }));
  };

  // Handle price change
  const handlePriceChange = (field, value) => {
    setFormData((prev) => {
      const newPrice = JSON.parse(JSON.stringify(prev.price)); // Deep copy
      if (field.startsWith("wh.")) {
        const key = field.replace("wh.", "");
        newPrice.wh[key] = value;
      } else if (field.startsWith("nwh.")) {
        const key = field.replace("nwh.", "");
        newPrice.nwh[key] = value;
      }
      return {
        ...prev,
        price: newPrice,
      };
    });
  };

  // Handle "Same as working hours" click
  const handleSameAsWorkingHours = () => {
    setFormData((prev) => ({
      ...prev,
      price: {
        ...prev.price,
        nwh: { ...prev.price.wh },
      },
    }));
  };

  // Validation
  const validate = () => {
    if (!formData.title.en || !formData.title.en.trim()) {
      return false;
    }
    if (!formData.title.vi || !formData.title.vi.trim()) {
      return false;
    }
    if (!formData.service) {
      return false;
    }
    if (!formData.subService) {
      return false;
    }
    if (!formData.code || !formData.code.trim()) {
      return false;
    }
    return true;
  };

  const validatePriceFields = () => {
    const values = [
      formData.price.wh.opdInternationalPrice,
      formData.price.wh.opdLocalPrice,
      formData.price.wh.opdSubsidized,
      formData.price.nwh.opdInternationalPrice,
      formData.price.nwh.opdLocalPrice,
      formData.price.nwh.opdSubsidized,
    ];

    const isValid = values.every((val) => {
      const str = String(val || "").trim();
      return str !== "" && /^[0-9]+$/.test(str);
    });

    if (!isValid) {
      Widgets.ToastPopup.error(t("priceValidation"));
      return false;
    }
    return true;
  };

  // Handle update
  const handleUpdate = () => {
    if (!validate()) return;
    if (!validatePriceFields()) return;

    const effectiveChecklist = pdfUrl || existingData?.checklist || "";
    if (!effectiveChecklist) {
      Widgets.ToastPopup.error(t("checklistRequired"));
      return;
    }

    // Prepare payload - merge with existing data
    const payload = {
      title: {
        en: formData.title.en.trim(),
        vi: formData.title.vi.trim(),
      },
      price: {
        ...existingData?.price,
        ...formData.price,
      },
      checklist: effectiveChecklist,
    };

    dispatch(updateServiceItem({ id, body: payload }));
  };

  // Handle delete
  const handleDelete = () => {
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (id) {
      const result = await dispatch(deleteServiceItem(id));
      setIsDeleteOpen(false);
      if (result?.meta?.requestStatus === "fulfilled") {
        Widgets.ToastPopup.success(
          t("serviceDeletedSuccessfully") || "Service deleted successfully"
        );
        navigate(strings.navigation.nursingCareManageService);
      }
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteOpen(false);
  };

  const currentServiceGroup = services.find((s) => s._id === (item?.service?._id || formData.service));
  const isGroupActive = currentServiceGroup ? currentServiceGroup.isActive : true;

  const handleToggleGroupStatus = async () => {
    // Ensure we are getting the string ID of the service group
    const serviceGroupId = selectedService?.value || (typeof formData.service === 'string' && formData.service !== "[object Object]" ? formData.service : null);

    if (!serviceGroupId) {
      Widgets.ToastPopup.error(t("selectServiceGroupError") || "Invalid service group ID");
      return;
    }

    const result = await dispatch(deleteService(serviceGroupId));
    
    if (result?.meta?.requestStatus === "fulfilled") {
      Widgets.ToastPopup.success(
        t("serviceGroupStatusUpdated") || "Service group status updated successfully"
      );
      dispatch(fetchServices({ page: 1, limit: 100 }));
    }
  };

  // Dedupe API/state duplicates before rendering read-only dropdown options.
  const serviceOptions = useMemo(
    () =>
      uniqueByLocalizedTitle(
        services.filter((s) => s.isActive),
        activeLang
      )
        .map((s) => ({
          value: s._id,
          label: getLocalizedTitle(s.title, activeLang),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
    [services, activeLang]
  );

  // Keep each visible sub-service group once while preserving the selected ID.
  const subServiceOptions = useMemo(
    () =>
      uniqueByLocalizedTitle(
        subServices.filter((s) => s.isActive),
        activeLang
      )
        .map((s) => ({
          value: s._id,
          label: getLocalizedTitle(s.title, activeLang),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
    [subServices, activeLang]
  );

  // Get selected service type option
  const selectedServiceType = serviceTypeOptions.find(
    (opt) => opt.value === formData.price.type
  );

  // Get selected service option
  const selectedService =
    serviceOptions.find((opt) => opt.value === formData.service) ||
    (() => {
      const service = services.find((s) => s._id === formData.service);
      return service
        ? { value: service._id, label: getLocalizedTitle(service.title, activeLang) }
        : null;
    })();

  // Get selected sub-service option
  const selectedSubService =
    subServiceOptions.find((opt) => opt.value === formData.subService) ||
    (() => {
      const subService = subServices.find((s) => s._id === formData.subService);
      return subService
        ? { value: subService._id, label: getLocalizedTitle(subService.title, activeLang) }
        : null;
    })();

  // Get selected language option
  const selectedLang = languageOptions.find((opt) => opt.value === activeLang);

  const breadcrumbs = ["nursingCare", "manageService", "serviceDetails"];
  const paths = [
    strings.navigation.nursingCareManageService,
    strings.navigation.nursingCareManageService,
    strings.navigation.serviceDetail,
  ];

  // Get service item title for display
  const serviceItemTitle = existingData?.title
    ? getLocale(existingData.title)
    : t("serviceDetails") || "Service Details";

  return (
    <div className="mr-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="department-heading-row">
        <div className="manage-service-heading">{serviceItemTitle}</div>
        
      </div>

      <div className="create-service-main">
        {/* Language Dropdown - Top Right */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "2rem" }}>
          <div className="">
            <Widgets.NewSelectDropdown
              options={languageOptions}
              big
              placeholder={t("selectLanguage") || "Select Language"}
              onChange={(option) => setActiveLang(option.value)}
              selectedValue={selectedLang}
            />
          </div>
        </div>

        {/* Service Details Form Section */}
        <div className="service-type-main">
          {/* Service Type */}
          <div className="service-input-box nc-readonly-block">
            <span>
              {t("serviceType")} <label>*</label>
            </span>
            <Widgets.NewSelectDropdown
              options={serviceTypeOptions}
              selectedValue={selectedServiceType}
              onChange={handleServiceTypeChange}
              placeholder={t("selectServiceType") || "Select Service Type"}
              big
            />
          </div>

          <div className="group-input-box">
            {/* Service Group */}
            <div className="service-input-box">
              <span>
                {t("serviceGroup")}{activeLang === "en" ? <>(En)</> : <>(Vi)</>} <label>*</label>
              </span>
              <div className="nc-readonly-block">
                <Widgets.NewSelectDropdown
                  options={serviceOptions}
                  selectedValue={selectedService}
                  onChange={handleServiceChange}
                  placeholder={t("selectServiceGroup") || "Select Service Group"}
                  big
                />
              </div>
            </div>

            {/* Sub-Service Group */}
            <div className="service-input-box">
              <span>
                {t("subServiceGroup")}{activeLang === "en" ? <>(En)</> : <>(Vi)</>} <label>*</label>
              </span>
              <div className="nc-readonly-block">
                <Widgets.NewSelectDropdown
                  options={subServiceOptions}
                  selectedValue={selectedSubService}
                  onChange={handleSubServiceChange}
                  placeholder={t("selectSubServiceGroup") || "Select Sub-Service Group"}
                  big
                  disabled={!formData.service}
                />
              </div>
            </div>

            {/* Service Item Title */}
            <div className="service-input-box">
              <span>
                {t("serviceItem")} {activeLang === "en" ? <>(En)</> : <>(Vi)</>} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.title[activeLang]}
                onChange={handleTitleChange}
                placeholder={
                  activeLang === "en"
                    ? t("enterServiceItemTitle") || "Enter service item title (English)"
                    : t("enterServiceItemTitleVi") || "Enter service item title (Vietnamese)"
                }
                className="form-section-input-2 nc-readonly-input"
                disabled
                maxLength={100}
              />
            </div>
          </div>

          {/* Service Code */}
          <div className="service-input-box">
            <span>
              {t("serviceCode")} <label>*</label>
            </span>
            <input
              type="text"
              value={formData.code}
              onChange={handleCodeChange}
              placeholder={t("enterServiceCode") || "Enter service item code"}
              className="form-section-input-2 nc-readonly-input"
              disabled
            />
          </div>
        </div>

        {/* Price Working Hour Section */}
        <div className="whp-container">
          <div className="title-hr">
            <p>{t("priceWorkingHour") || "Set price for working hours"}</p>
            <hr />
          </div>
          <div className="service-type-main2">
            <div className="service-input-box">
              <span>
                {t("opdInterInsured")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.wh.opdInternationalPrice}
                onChange={(e) =>
                  handlePriceChange("wh.opdInternationalPrice", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
            <div className="service-input-box">
              <span>
                {t("opdLocalInsured")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.wh.opdLocalPrice}
                onChange={(e) =>
                  handlePriceChange("wh.opdLocalPrice", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
            <div className="service-input-box">
              <span>
                {t("opdSubsidized")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.wh.opdSubsidized}
                onChange={(e) =>
                  handlePriceChange("wh.opdSubsidized", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
          </div>
        </div>

        {/* Price Non-Working Hour Section */}
        <div className="whp-container">
          <div className="title-hr2">
            <p>{t("priceNonWorkingHour") || "Set price for non-working hours"}</p>
            <hr />
          </div>
          <p className="clickable-text" onClick={handleSameAsWorkingHours}>
            {t("sameAsWorkingHour") || "Same as working hours"}
          </p>
          <div className="service-type-main2">
            <div className="service-input-box">
              <span>
                {t("opdInterInsured")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.nwh.opdInternationalPrice}
                onChange={(e) =>
                  handlePriceChange("nwh.opdInternationalPrice", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
            <div className="service-input-box">
              <span>
                {t("opdLocalInsured")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.nwh.opdLocalPrice}
                onChange={(e) =>
                  handlePriceChange("nwh.opdLocalPrice", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
            <div className="service-input-box">
              <span>
                {t("opdSubsidized")} <label>*</label>
              </span>
              <input
                type="text"
                value={formData.price.nwh.opdSubsidized}
                onChange={(e) =>
                  handlePriceChange("nwh.opdSubsidized", e.target.value)
                }
                placeholder="Vnd 0"
                className="form-section-input-2"
              />
            </div>
          </div>
        </div>

        {/* Checklist PDF - read-only preview */}
        <div className="pdf-upload-container">
          <span className="pdf-heading">
            {t("uploadChecklistPdf")} <label>*</label>
          </span>
          {pdfUrl && (
            <div className="service-pdf-wrapper">
              <div
                className="service-pdf-card service-pdf-preview"
                onClick={() => window.open(pdfUrl, "_blank")}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <img
                    src={Icons.PdfRedIcon}
                    className="service-pdf-icon"
                    alt="Checklist PDF"
                  />
                  <span className="service-pdf-name">Checklist.pdf</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form Footer */}
        <div className="form-section-footer">
          <button
            type="button"
            className="form-section-cancel"
            onClick={() => navigate(strings.navigation.nursingCareManageService)}
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            className={`form-section-create ${!isPriceDirty ? "disabled-btn" : ""}`}
            onClick={handleUpdate}
            disabled={!isPriceDirty}
          >
            {t("update") || "Update"}
          </button>
        </div>
      </div>

      <Widgets.ConfirmModal
        showModal={isDeleteOpen}
        handleCancel={handleCancelDelete}
        handleConfirm={handleConfirmDelete}
        heading={t("deleteService") || "Delete Service"}
        description={t("deleteServiceConfirm") || "Are you sure to delete this service"}
      />
    </div>
  );
};

export default ManageRequestServiceDetails;
